/**
 * Geometry standard library for the proof. grammar (Track 2.A).
 *
 * Implements §4.3 of extensibility-design.md:
 *   cylinder(radius, height)        → volume (ml/cm³)
 *   rect(length, width, height)     → volume (ml/cm³)
 *   ring(outer_r, inner_r, height)  → volume (ml/cm³)
 *   sphere(radius)                  → volume (ml/cm³)
 *
 * All operands must be length-dimension values. Results are in ml (= cm³).
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import { dimensionOf, toCanonicalValue } from "../units/dimensions.js";
import type { ArithmeticResult } from "../units/arithmetic.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ok(value: number): ArithmeticResult {
  return { ok: true, value: { value, unit: "ml" } };
}

function typeMismatch(message: string): ArithmeticResult {
  return { ok: false, error: { code: "type-mismatch", message } };
}

/** Convert a value to canonical cm, asserting it is a length dimension. */
function toCm(value: number, unit: string, argName: string): { cm: number } | ArithmeticResult {
  const dim = dimensionOf(unit);
  if (dim !== "length") {
    return typeMismatch(
      `${argName} must be a length value (got ${unit || "dimensionless"} — ${dim})`,
    );
  }
  return { cm: toCanonicalValue(value, unit) };
}

function isError(r: object): r is ArithmeticResult {
  return "ok" in r && (r as ArithmeticResult).ok === false;
}

// ---------------------------------------------------------------------------
// cylinder(radius, height) → π × r² × h
// ---------------------------------------------------------------------------

/**
 * Compute the volume of a cylinder.
 *
 * @param radius radius TypedValue (must be length)
 * @param height height TypedValue (must be length)
 * @returns volume in ml (cm³)
 *
 * Example: cylinder(11cm, 5cm) → ~1900.66 ml
 */
export function cylinder(
  radius: { value: number; unit: string },
  height: { value: number; unit: string },
): ArithmeticResult {
  const r = toCm(radius.value, radius.unit, "radius");
  if (isError(r)) return r as ArithmeticResult;
  const h = toCm(height.value, height.unit, "height");
  if (isError(h)) return h as ArithmeticResult;
  return ok(
    Math.PI * (r as { cm: number }).cm * (r as { cm: number }).cm * (h as { cm: number }).cm,
  );
}

// ---------------------------------------------------------------------------
// rect(length, width, height) → l × w × h
// ---------------------------------------------------------------------------

/**
 * Compute the volume of a rectangular prism.
 *
 * Example: rect(20cm, 20cm, 5cm) → 2000 ml
 */
export function rect(
  length: { value: number; unit: string },
  width: { value: number; unit: string },
  height: { value: number; unit: string },
): ArithmeticResult {
  const lv = toCm(length.value, length.unit, "length");
  if (isError(lv)) return lv as ArithmeticResult;
  const wv = toCm(width.value, width.unit, "width");
  if (isError(wv)) return wv as ArithmeticResult;
  const hv = toCm(height.value, height.unit, "height");
  if (isError(hv)) return hv as ArithmeticResult;
  return ok((lv as { cm: number }).cm * (wv as { cm: number }).cm * (hv as { cm: number }).cm);
}

// ---------------------------------------------------------------------------
// ring(outer_r, inner_r, height) → π × (outer_r² - inner_r²) × h
// ---------------------------------------------------------------------------

/**
 * Compute the volume of a ring (annular cylinder).
 *
 * Example: ring(11cm, 5cm, 5cm)
 */
export function ring(
  outerR: { value: number; unit: string },
  innerR: { value: number; unit: string },
  height: { value: number; unit: string },
): ArithmeticResult {
  const ro = toCm(outerR.value, outerR.unit, "outer_r");
  if (isError(ro)) return ro as ArithmeticResult;
  const ri = toCm(innerR.value, innerR.unit, "inner_r");
  if (isError(ri)) return ri as ArithmeticResult;
  const h = toCm(height.value, height.unit, "height");
  if (isError(h)) return h as ArithmeticResult;
  const roCm = (ro as { cm: number }).cm;
  const riCm = (ri as { cm: number }).cm;
  const hCm = (h as { cm: number }).cm;
  return ok(Math.PI * (roCm * roCm - riCm * riCm) * hCm);
}

// ---------------------------------------------------------------------------
// sphere(radius) → (4/3) × π × r³
// ---------------------------------------------------------------------------

/**
 * Compute the volume of a sphere.
 *
 * Example: sphere(5cm) → ~523.6 ml
 */
export function sphere(radius: { value: number; unit: string }): ArithmeticResult {
  const r = toCm(radius.value, radius.unit, "radius");
  if (isError(r)) return r as ArithmeticResult;
  const rCm = (r as { cm: number }).cm;
  return ok((4 / 3) * Math.PI * rCm * rCm * rCm);
}

// ---------------------------------------------------------------------------
// Dispatch by function name
// ---------------------------------------------------------------------------

export type GeometryFnName = "cylinder" | "rect" | "ring" | "sphere";

export const GEOMETRY_FN_NAMES = new Set<string>(["cylinder", "rect", "ring", "sphere"]);

/**
 * Call a geometry stdlib function by name with evaluated arguments.
 * Returns an error result if the function name is not recognised or arity is wrong.
 */
export function callGeometry(
  name: string,
  args: readonly { value: number; unit: string }[],
): ArithmeticResult {
  switch (name) {
    case "cylinder": {
      if (args.length !== 2) {
        return {
          ok: false,
          error: { code: "type-mismatch", message: `cylinder expects 2 args, got ${args.length}` },
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return cylinder(args[0]!, args[1]!);
    }
    case "rect": {
      if (args.length !== 3) {
        return {
          ok: false,
          error: { code: "type-mismatch", message: `rect expects 3 args, got ${args.length}` },
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return rect(args[0]!, args[1]!, args[2]!);
    }
    case "ring": {
      if (args.length !== 3) {
        return {
          ok: false,
          error: { code: "type-mismatch", message: `ring expects 3 args, got ${args.length}` },
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return ring(args[0]!, args[1]!, args[2]!);
    }
    case "sphere": {
      if (args.length !== 1) {
        return {
          ok: false,
          error: { code: "type-mismatch", message: `sphere expects 1 arg, got ${args.length}` },
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return sphere(args[0]!);
    }
    default: {
      return {
        ok: false,
        error: { code: "type-mismatch", message: `Unknown geometry function: ${name}` },
      };
    }
  }
}
